import { Tag } from '@carbon/react';
import { Link, PlainLink } from 'components/link/link';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import { fmtAmount } from '../format';
import { CreatedByCell, SourceCell } from './cells';
import type { RoyaltyCheckResult } from './types';
import styles from './columns.module.scss';

export const SOURCE_FILTER_OPTIONS = [
  { value: 'Salesforce', label: 'Salesforce' },
  { value: 'Provisioned', label: 'Auto-Connected' },
];

export type RoyaltyCheckRow = RoyaltyCheckResult & Record<string, unknown>;

export const royaltyCheckColumns: DataTableColumnDef<RoyaltyCheckRow>[] = [
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Check Name',
    size: 200,
    meta: { type: 'text', wrap: true },
    cell: ({ row }) => (
      <div className={styles.nameCell}>
        <Link to={row.original.href}>{row.original.name}</Link>
        {row.original.conflictStatus === 'needs_review' && (
          <Tag
            type="magenta"
            size="sm"
            title="A Salesforce-sourced duplicate shares this check's conflict key and needs examiner review."
          >
            Needs review
          </Tag>
        )}
      </div>
    ),
  },
  {
    id: 'source',
    accessorKey: 'source',
    header: 'RC Input Source',
    size: 150,
    meta: { type: 'enum', filterOptions: SOURCE_FILTER_OPTIONS },
    cell: ({ row }) => (
      <SourceCell
        source={row.original.source}
        sourceDocument={row.original.sourceDocument}
      />
    ),
  },
  {
    id: 'lineItemCount',
    accessorKey: 'lineItemCount',
    header: 'Line Items',
    size: 100,
    meta: { type: 'integer' },
  },
  {
    id: 'propertyCount',
    accessorKey: 'propertyCount',
    header: 'Properties',
    size: 100,
    meta: { type: 'integer' },
  },
  {
    id: 'payorName',
    accessorFn: (row) => row.payor?.name ?? null,
    header: 'Payor',
    size: 200,
    cell: ({ row }) => {
      const { payor } = row.original;
      if (!payor?.name) return '--';
      if (payor.entityHref) {
        return <Link to={payor.entityHref}>{payor.name}</Link>;
      }
      if (payor.salesforceHref) {
        return (
          <PlainLink
            href={payor.salesforceHref}
            target="_blank"
            rel="noreferrer"
          >
            {payor.name}
          </PlainLink>
        );
      }
      return payor.name;
    },
  },
  {
    id: 'payeeName',
    accessorFn: (row) => row.payee?.name ?? null,
    header: 'Payee',
    size: 200,
    cell: ({ row }) => {
      const { payee } = row.original;
      if (!payee?.name) return '--';
      if (payee.entityHref) {
        return <Link to={payee.entityHref}>{payee.name}</Link>;
      }
      if (payee.salesforceHref) {
        return (
          <PlainLink
            href={payee.salesforceHref}
            target="_blank"
            rel="noreferrer"
          >
            {payee.name}
          </PlainLink>
        );
      }
      return payee.name;
    },
  },
  {
    id: 'checkNumber',
    accessorKey: 'checkNumber',
    header: 'Check Number',
    size: 130,
    meta: { type: 'text' },
  },
  {
    id: 'checkIssueDate',
    accessorKey: 'checkIssueDate',
    header: 'Check Issue Date',
    size: 140,
    meta: { type: 'date' },
  },
  {
    id: 'checkAmount',
    accessorKey: 'checkAmount',
    header: 'Check Amount',
    size: 140,
    meta: { type: 'decimal', align: 'right' },
    cell: ({ row }) => fmtAmount(row.original.checkAmount),
  },
  {
    id: 'createdAt',
    accessorKey: 'createdAt',
    header: 'Created At',
    size: 160,
    meta: { type: 'datetime' },
  },
  {
    id: 'updatedAt',
    accessorKey: 'updatedAt',
    header: 'Updated At',
    size: 160,
    meta: { type: 'datetime' },
  },
  {
    id: 'createdByName',
    accessorKey: 'createdByName',
    header: 'Created By',
    size: 160,
    cell: ({ row }) => (
      <CreatedByCell
        source={row.original.source}
        createdByName={row.original.createdByName}
      />
    ),
  },
];

export { DataTable };
